/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";
import { safeLocalStorage } from "@/helper/safeLocalStorage";
import { ALL_LANGUAGES, type SupportedLanguage } from "@/lib/constants";
import {
  LANGUAGE_QUERY_PARAM,
  getLanguageFromBrowser,
  getLanguageFromQuery,
  normalizeLocalizedPath,
} from "@/utils/localizedPath";
// Item 13 (Session 361): English travels with the code. See the init options
// below for why. The path reaches outside src/ because the file is also served
// as a static asset for the five other languages — one file, two consumers.
import enTranslation from "../public/locales/en.json";

// DD-12: Re-export for backward compatibility — existing callers import from here
export const SUPPORTED_LANGUAGES = ALL_LANGUAGES;
export type { SupportedLanguage };

// Safely get the stored language — uses safeLocalStorage for consistent
// SSR safety, error handling, and Sentry reporting across the codebase.
//
// N-11 (Session 376): this returns null when nothing is stored. It used to
// return "en", which is indistinguishable from a visitor who has actually
// chosen English — so every step placed after it in the resolution order was
// unreachable, and reordering alone would have looked like it did nothing.
const getStoredLanguage = (): SupportedLanguage | null => {
  const stored = safeLocalStorage.getItem("lang");
  if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
    return stored as SupportedLanguage;
  }
  return null;
};

// Detect language from URL path (e.g., /de/product-page)
const getLanguageFromPath = (): SupportedLanguage | null => {
  if (typeof window !== "undefined") {
    const { lang } = normalizeLocalizedPath(window.location.pathname);
    return lang as SupportedLanguage | null;
  }
  /* c8 ignore next 2 */
  // SSR guard: window is always defined in jsdom and in the browser;
  // this branch only fires in true server-side environments.
  return null;
};

/*
 * VS-1 (Session 376). URL path > explicit ?lang= on the link > the preference
 * this visitor chose > what their browser asks for > English.
 *
 * A stored preference is a choice the customer made and outranks a browser
 * hint; a link that states a language outranks both, because whoever sent the
 * link knew which language it was for. The browser step is new and sits
 * deliberately BELOW the stored preference — see getLanguageFromBrowser for why
 * putting it above breaks the site's own language switcher.
 *
 * The English fallback is explicit now that getStoredLanguage can return null.
 */
const getInitialLanguage = (): SupportedLanguage =>
  (getLanguageFromPath() ||
    getLanguageFromQuery() ||
    getStoredLanguage() ||
    getLanguageFromBrowser() ||
    "en") as SupportedLanguage;

/**
 * Honour an explicit language once, then take it out of the address.
 *
 * The same treatment the two password pages already give the email address and
 * the reset key, for the same reasons: it keeps the value out of browser
 * history and out of referrer headers, and it stops a link somebody shares from
 * silently switching the recipient's language for the rest of their visit.
 *
 * Writing it to the stored preference first is what makes the language survive
 * the rewrite — and it is also what stops the startup geolocation lookup
 * guessing a language when the link has already stated one.
 */
export const honourLanguageQueryParam = (): void => {
  if (typeof window === "undefined") return;
  /* c8 ignore next 2 */
  const stated = getLanguageFromQuery();
  if (!stated) return;

  safeLocalStorage.setItem("lang", stated);

  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(LANGUAGE_QUERY_PARAM);
    const qs = url.searchParams.toString();
    const clean = url.pathname + (qs ? `?${qs}` : "") + url.hash;
    window.history.replaceState(window.history.state, "", clean);
  } catch {
    // No-op: invalid URL (e.g. empty href in test environments). The language
    // has already been honoured and stored; only the tidy-up is skipped.
  }
};

i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    // Item 13 (Session 361): English travels with the code.
    //
    // A key renders as its own name in exactly one situation — the library was
    // asked for a key that is not in the file it loaded. That happens when a
    // browser runs a freshly deployed bundle against a translation file it
    // cached before the deploy, and it is what produced the raw key names in
    // the report. English is the fallback for every other language, so with
    // English inside the bundle it can never be out of step with the code that
    // reads it, and one blocking request disappears from first paint.
    //
    // partialBundledLanguages is what allows both at once: the other five
    // still load over the network through the backend below.
    resources: { en: { translation: enTranslation } },
    partialBundledLanguages: true,

    // Load locale files dynamically from public folder
    backend: {
      // The build stamp makes each release ask for an address no browser has
      // cached, so a stale file cannot be paired with new code however the
      // server in front is configured — which matters here because the server
      // configuration held in this repository is not the one production runs.
      // The value only has to change once per release; it is neither secret
      // nor a hash of the file.
      loadPath: `/locales/{{lng}}.json?v=${__BUILD_ID__}`,
    },
    lng: getInitialLanguage(),
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES,

    // Only load the current language, not all languages
    preload: [], // Don't preload any languages

    // Loading behavior
    load: "currentOnly", // Only load current language, not language variants

    interpolation: {
      escapeValue: false,
    },

    // React specific options
    react: {
      useSuspense: true, // Enable Suspense for loading states
    },

    // Debug mode (disable in production)
    debug: import.meta.env.DEV,
  });

// Runs once, immediately after the language has been resolved from it.
honourLanguageQueryParam();

export default i18n;
